import React, { useMemo, useState } from "react";
import "./styles.css";
import { Breadcrumb, OptionList, PopUpModal } from "../../Components/Atoms";
import PATHS from "../../Routes/paths";
import { ChevronRightIcon, CloseIcon, CloudUploadIcon, FileIcon, InProgressIcon, RoundCrossIcon } from "../../Assets/images/svgs";
import { jsonToCSVConvertor } from "../../Library/jsonUtils";
import { ReactTable } from "../../Components/Molecules";
import { VIEW_UPLOADED_VEHS } from "../../Library/tableHeaders";
import { Button, Input, Tooltip, useToast } from "@chakra-ui/react";
import { performBulkOperation } from "../../APIs";
import { useNavigate } from "react-router-dom";
import { ellipsisify } from "../../Library/helpers";

const NewBulkOperation = () => {

  const breadcrumbPaths = [
    { idx: 1, name: "Bulk Operations", link: `${PATHS.BULK_OPERATIONS}` },
    { idx: 3, name: "Perform New Operation", isCurrentPage: true },
  ];

  const ReportsDownloadOptions = [
    { label: "Immobilise Vehicles", value: 1 },
    { label: "Mobilise Vehicles", value: 2 },
  ];

  const lockUnlockLabelMap = {
    1: "Immobilise",
    2: "Mobilise",
  }

  const [selectedOperation, setSelectedOperation] = useState({});
  const [showDragOverlay, setShowDragOverlay] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [uploadedData, setUploadedData] = useState([]);
  const [uploadedFile, setUploadedFile] = useState({});
  const [showDataModal, setShowDataModal] = useState(false);
  const [popUpModal, setPopUpModal] = useState({});
  const [operationName, setOperationName] = useState("");

  const Toast = useToast();
  const navigate = useNavigate();

  const createVehListTemplate = () => {
    let templateHeadings = ["Vehicle Number", "Chassis Number"];
    jsonToCSVConvertor([templateHeadings], "", null, "VehicleListTemplate");
  };

  const processPipeSeparatedData = (rawData) => {
    const lines = rawData.trim().split('\n');
    const result = [];

    for (let i = 1; i < lines.length; i++) {
      const [vehNo, chasisNo] = lines[i].split('|').map(item => item.trim());
      if (vehNo && chasisNo) {
        result.push({ srNo: i, vehNo, chasisNo });
      } else {
        if (!vehNo && !chasisNo) continue;

        if (!vehNo) return "Registration number missing";
        else if (!chasisNo) return "Chassis number missing";
      }
    }

    return result;
  };


  const convertCsvToJson = (file) => {
    const csvReader = new FileReader();

    csvReader.onload = (e) => {
      const text = e.target.result;
      const modifiedText = text.replace(/(".*?")|,/g, (...m) => m[1] || "|");
      let data = processPipeSeparatedData(modifiedText.replaceAll('"', ""));
      if (typeof data === "string") {
        setUploadError(data);
      } else if (!data || !data.length) {
        setUploadError("Uploaded file seems empty!");
      } else setUploadedData(data);
    };
    csvReader.readAsText(file);
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    let uploadedFile;
    if (e.dataTransfer.items[0].kind === "file") {
      uploadedFile = e.dataTransfer.items[0].getAsFile();
    }

    const isCSV = uploadedFile.type === "text/csv";
    if (!isCSV) {
      setShowDragOverlay(false);
      setUploadError("Invalid file type. Only csv files allowed");
      return;
    }

    setShowDragOverlay(false);
    setUploadError("");
    setUploadedFile(uploadedFile);
    convertCsvToJson(uploadedFile);
  }

  const columns = useMemo(() => {
    const cols = [];
    Object.values(VIEW_UPLOADED_VEHS).forEach(header => {
      cols.push({
        Header: header.nm,
        accessor: header.key,
        Cell: (row) => row.value,
      });
    });
    return cols;
  }, []);

  const removeUploadedFile = () => {
    setUploadedData([]);
    setUploadedFile({});
    setUploadError("");
  }

  const handlePerformOperation = () => {
    let data = { oprType: selectedOperation.value, oprNm: operationName, vehData: uploadedData.map(veh => { return { vehNo: veh.vehNo, chasisNo: veh.chasisNo }}) };
    performBulkOperation(data, (err, data) => {
      if (err) Toast({ isClosable: true, description: err, position: "bottom", variant: "subtle", duration: 3000 });
      else {
        setPopUpModal({
          open: true,
          modalMsg: (
            <div className="operation-success-container">
              <InProgressIcon />
              <p>Bulk Operation Started</p>
            </div>
          ),
          onModalClose: () => {
            setPopUpModal(null);
            navigate(PATHS.BULK_OPERATIONS);
          },
          confirmBtn: "View Summary",
          onConfirm: () => navigate(`${PATHS.BULK_OPERATIONS}${PATHS.OPERATION_SUMMARY}?oprId=${data.oprId}`),
        });
      }
    });
  }

  const handleConfirmation = () => {
    if (!selectedOperation.label || !operationName || !uploadedData || !uploadedData.length) return;

    setPopUpModal({
      open: true,
      modalMsg: <p className="new-operation-confirm-msg">This operation will {lockUnlockLabelMap[selectedOperation.value]} {uploadedData.length} vehicles. Are you sure you want to continue?</p>,
      onModalClose: () => setPopUpModal(null),
      closeBtn: "Cancel",
      confirmBtn: "Continue",
      onConfirm: () => handlePerformOperation(),
    });
  }

  const handleFileSelect = (e) => {
    let selectedFile = e.target.files[0];
    const isCSV = selectedFile.type === "text/csv";
    if (!isCSV) {
      setShowDragOverlay(false);
      setUploadError("Invalid file type. Only csv files allowed");
      return;
    }

    setUploadedFile(selectedFile);
    convertCsvToJson(selectedFile)
  }

  return (
    <section className="panel-main new-bulk-operation">
      <header className="order-details-header">
        <Breadcrumb paths={breadcrumbPaths} />
      </header>

      <div className="operation-type-selector">
        <p className="operation-field-label">Operation Type</p>
        <OptionList customClassName={`select-operation-type ${selectedOperation.value ? "selected" : ""}`} name="Select Operation Type" selected={selectedOperation.label || ""} options={ReportsDownloadOptions} onOptionClick={(a) => setSelectedOperation(a)} />
      </div>

      <div className="operation-name-container">
        <p className="operation-field-label">Operation Name</p>
        <Input placeholder="Give this operation a name" className="operation-name-field" onChange={(e) => setOperationName(e.target.value)} />
      </div>

      <div className="upload-vehicle-list">
        <p className="operation-field-label">Upload Vehicle List</p>
        <div className="uploader-container">
          {!(uploadedData && uploadedData.length) ? <>
            <p className="uploader-title">
              Dont't have the template?
              <span className="downloadTemplate" onClick={createVehListTemplate}>
                Click Here!
              </span>
            </p>
            <p className="uploader-note">*Make sure you are using vehicle template file</p>
          </> : <div className="uploaded-file-box">
            <FileIcon className="uploaded-file-icon" />
            <div className="loader-container">
              <Tooltip label={uploadedFile.name && uploadedFile.name.length >= 30 ? uploadedFile.name : ""}>
                <p className="file-name">{uploadedFile.name ? ellipsisify(uploadedFile.name, 30) : "Upload.csv"}</p>
              </Tooltip>
              <div className="loader-bar">
                <div className="progress-bar"></div>
              </div>
              <div className="file-details">
                <p className="file-size">{uploadedFile.size ? `${(uploadedFile.size / (1024 * 1024)).toFixed(4)} MB` : ""}</p>
                <p className="file-uploaded-hint">Uploaded</p>
              </div>
            </div>
            <CloseIcon onClick={removeUploadedFile} className="uploaded-file-cross-icon" />
          </div>}
          {!(uploadedData && uploadedData.length) ? <label className={`upload-box ${showDragOverlay ? "show-overlay" : ""}`} onDragOver={(e) => { e.preventDefault(); setUploadError(""); setShowDragOverlay(true); }} onDragLeave={(e) => { e.preventDefault(); setUploadError(""); setShowDragOverlay(false) }} onDrop={handleFileDrop}>
            {!showDragOverlay ? <>
              <CloudUploadIcon className="cloud-upload-icon-veh-list" />
              {uploadError ? <p className="uploader-hint error">{uploadError}</p> : <p className="uploader-hint">Drag file here or <span>Browse</span> for file upload</p>}
            </> : <p className="drag-overlay-text">Drop you file here</p>}
            <input onChange={(e) => handleFileSelect(e)} type="file" className="upload-file-input" />
          </label> : <div className="uploaded-data-container">
            <ReactTable
              columns={columns.slice(1)}
              data={(uploadedData?.length > 3 ? uploadedData.slice(0, 3) : uploadedData) || []}
              pageSize={3}
            />
            {(uploadedData && uploadedData.length > 3) ? <div className="preview-btn-container">
              <Button onClick={() => setShowDataModal(true)} variant={"link"} className="file-preview-button">
                Preview complete file <ChevronRightIcon />
              </Button>
            </div> : null}
          </div>}
        </div>
      </div>

      <div className="perform-operation-btn-container">
        <Button onClick={handleConfirmation} variant={"solid"} className={`perform-operation-btn ${(selectedOperation.value && (uploadedData && uploadedData.length)) ? "" : "disable-operation-btn"}`}>
          Perform Operation
        </Button>
      </div>

      {showDataModal ? <div className="file-previewModal-overlay">
        <div className="modal-cross-icon-container">
          <RoundCrossIcon onClick={() => setShowDataModal(false)} className="modal-cross-icon" />
        </div>
        <div className="preview-file-table-container">
         <ReactTable
           columns={columns}
           data={uploadedData || []}
           pageSize={10000}
           showSearch={true}
         />
        </div>
      </div> : null}

      <PopUpModal {...popUpModal} />
    </section>
  )
}

export default NewBulkOperation
